import type { FundraiserProfile as FundraiserProfileProps, UpdateProfileInput } from '@web/graphql-components'
import { FundraiserProfile as PageFundraiserProfile } from '@web/components/page-components/FundraiserProfile'
import SubComponent from '../SubComponent'
import useGraphQLClient from 'apps/fe-donation/libs/hooks/useGraphQLClient'
import { useMutation, useQuery } from '@tanstack/react-query'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { ProfileDataQuery, UpdateProfileMutation } from '@web/graphql-queries/graphql'
import { profileDataDocument } from 'apps/fe-donation/libs/queries'
import { useEffect } from 'react'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import { useActionDialogStore } from '@web/components/molecules/ActionDialog'
import { PageButton } from '@web/components/page-components/PageButton'
import { VStack } from '@web/components/atoms/Layout'
import UpdateFundraiserProfileForm from './UpdateFundraiserProfileForm'
import { useHttpClient } from 'apps/fe-donation/libs/hooks/useHttpClient'

const PROFILE_DATA_QUERY_KEY = 'profileData'

const FundraiserProfile = (props: FundraiserProfileProps) => {
  const charityId = usePageStore((state) => state.charityId)
  const campaignId = usePageStore((state) => state.campaignId)
  const profileId = usePageStore((state) => state.profileId)

  const graphQLClient = useGraphQLClient()
  const httpClient = useHttpClient({ isRelativeUrl: true })
  const { data: profileData, isFetching } = useQuery({
    queryKey: [PROFILE_DATA_QUERY_KEY, charityId, campaignId, profileId],
    queryFn: () =>
      graphQLClient<ProfileDataQuery>({
        document: profileDataDocument,
        options: { charityId, profileId, campaignId },
      }),
    select: (data) => data.profileData,
    enabled: !!profileId,
  })
  const isLoggedInProfile = usePageStore((state) => state.isLoggedInProfile)

  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: (input: UpdateProfileInput) => {
      const state = usePageStore.getState()
      if (!state.actions.getHasLoggedIn()) {
        return Promise.reject()
      }

      return httpClient<UpdateProfileMutation>('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ input, charityId: state.charityId }),
      })
    },
  })

  useEffect(() => {
    if (!profileData) {
      return
    }

    const setMergeField = useMergeFields.getState().setMergeField

    setMergeField('Profile.Name', profileData.name)

    if (profileData.description) {
      setMergeField('Profile.Bio', profileData.description)
    }
  }, [profileData, isFetching])

  return (
    <>
      <PageFundraiserProfile
        {...props}
        SubComponent={SubComponent}
        name={profileData?.name}
        avatarUrl={profileData?.logoUrl ?? ''}
        isLoading={isFetching}
      />
      {isLoggedInProfile && (
        <VStack w="100%" alignItems="start" gap="16px" marginTop="16px">
          <PageButton
            id="manage-profile"
            name="Primary"
            onClick={() =>
              useActionDialogStore.getState().actions.openDialog(
                {
                  title: 'Manage Profile',
                  content: (
                    <UpdateFundraiserProfileForm
                      profileData={{ id: '', ...profileData, name: profileData?.name ?? '' }}
                    />
                  ),
                  ctaText: 'Save',
                  type: 'action',
                  withCancelButton: false,
                },
                async (doAction, e) => {
                  if (!doAction || !e) {
                    return
                  }

                  const state = usePageStore.getState()
                  const formData = new FormData(e.target as HTMLFormElement)

                  const data: Partial<UpdateProfileInput> = {}

                  formData.forEach(
                    (value, key) =>
                      (data[key] = key == 'isGivingNotificationEnabled' ? (value == 'true' ? true : false) : value),
                  )

                  const { name, goal, description, logoUrl, isGivingNotificationEnabled } = data

                  await updateProfile({
                    campaignId: state.campaignId,
                    profileId: state.profileId!,
                    name,
                    goal: Number(goal) ? Number(goal) : 0,
                    description,
                    logoUrl,
                    isGivingNotificationEnabled: !!isGivingNotificationEnabled,
                  })

                  window.location.reload()

                  return true
                },
              )
            }
            buttonText="Manage Profile"
            width="100%"
          />
          <PageButton
            id="logout-profile"
            name="Secondary"
            onClick={() => {
              const state = usePageStore.getState()
              fetch('/api/profile/login', {
                method: 'DELETE',
              })
              state.actions.setHasLoggedIn(false)
              usePageStore.setState({
                isLoggedIn: false,
                isLoggedInProfile: false,
              })
            }}
            buttonText="Log out"
            width="100%"
          />
        </VStack>
      )}
    </>
  )
}

export default FundraiserProfile
